import { Controller } from "@hotwired/stimulus"
import TableFrameNavigator from "../../utils/table_frame_navigator"
import TableQueryState from "../../utils/table_query_state"

// Connects to data-controller="expenses--filter"
// Handles filter logic and URL construction for expenses
export default class extends Controller {
  static targets = [
    "orderNumber",
    "orderNumberEmpty",
    "project",
    "createdBy",
    "approvalWorkflow",
    "expenseType",
    "paymentStatus",
    "search",
    "createdFrom",
    "createdTo",
    "issuedFrom",
    "issuedTo",
    "amountFrom",
    "amountTo"
  ]
  static outlets = ["expenses--filter-menu"]

  // expenseType and paymentStatus are intentionally NOT here — they are
  // multi-checkbox groups handled separately via the expenseType[] and
  // paymentStatus[] array params.
  static singleValueTargets = [
    "orderNumber",
    "project",
    "createdBy",
    "approvalWorkflow",
    "search",
    "createdFrom",
    "createdTo",
    "issuedFrom",
    "issuedTo",
    "amountFrom",
    "amountTo"
  ]

  connect() {
    this.frameId = this.element.closest("turbo-frame#expenses_list")?.id || "expenses_list"
    this.tableNavigator = new TableFrameNavigator({
      frameId: this.frameId,
      tableKey: this.element.closest(".documents-table-controller")?.dataset.tableNavigationTableKeyValue || "expenses",
      onPopState: (queryState) => {
        this.setFilterValues(queryState)
      }
    })
    this.boundNavigationRollback = this.#handleNavigationRollback.bind(this)
    document.addEventListener("lucanto:table-navigation-rollback", this.boundNavigationRollback)
    this.setFilterValues()
  }

  disconnect() {
    document.removeEventListener("lucanto:table-navigation-rollback", this.boundNavigationRollback)
    this.tableNavigator?.disconnect()
  }

  setFilterValues(queryState = new TableQueryState(window.location.search)) {
    const urlSearchParams = queryState.toSearchParams()
    this.constructor.singleValueTargets.forEach((targetName) => {
      const hasTargetProperty = `has${targetName.charAt(0).toUpperCase() + targetName.slice(1)}Target`
      if (this[hasTargetProperty]) {
        const target = this[`${targetName}Target`]
        if (target) {
          target.value = urlSearchParams.get(targetName) || ""
          target.disabled = false
        }
      }
    })

    // Handle empty order number checkbox
    if (this.hasOrderNumberEmptyTarget) {
      this.orderNumberEmptyTarget.checked = urlSearchParams.get("orderNumber") === "empty"
    }
    if (this.hasOrderNumberEmptyTarget && this.orderNumberEmptyTarget.checked) {
      if (this.hasOrderNumberTarget) {
        this.orderNumberTarget.value = ""
        this.orderNumberTarget.disabled = true
      }
    }

    // Restore the canonical repeated parameter and legacy scalar URLs/saved views.
    this.#restoreCheckboxGroup(urlSearchParams, "paymentStatus", this.paymentStatusTargets)
    this.#restoreCheckboxGroup(urlSearchParams, "expenseType", this.expenseTypeTargets)
  }

  toggleEmptyOrderNumber() {
    if (this.hasOrderNumberEmptyTarget && this.hasOrderNumberTarget) {
      if (this.orderNumberEmptyTarget.checked) {
        this.orderNumberTarget.value = ""
        this.orderNumberTarget.disabled = true
      } else {
        this.orderNumberTarget.disabled = false
      }
    }
  }

  clearEmptyOrderNumber() {
    if (this.hasOrderNumberEmptyTarget && this.orderNumberTarget.value) {
      this.orderNumberEmptyTarget.checked = false
    }
  }

  activeStatusChecker(event) {
    const filterMenuController = this.expensesFilterMenuOutlet

    // Multi-checkbox groups — active when ANY box is checked
    if (event.target.name.includes("paymentStatus")) {
      const anyChecked = this.#checkedValues(this.paymentStatusTargets).length > 0
      filterMenuController.toggleActiveStatus(anyChecked, filterMenuController.activeStatusPaymentStatusTarget)
      return
    }
    if (event.target.name.includes("expenseType")) {
      const anyChecked = this.#checkedValues(this.expenseTypeTargets).length > 0
      filterMenuController.toggleActiveStatus(anyChecked, filterMenuController.activeStatusExpenseTypeTarget)
      return
    }

    const mapping = {
      orderNumber: [
        ["orderNumberTarget", "orderNumberEmptyTarget"],
        filterMenuController.activeStatusOrderNumberTarget
      ],
      project: [["projectTarget"], filterMenuController.activeStatusProjectTarget],
      createdBy: [["createdByTarget"], filterMenuController.activeStatusCreatedByTarget],
      approvalWorkflow: [["approvalWorkflowTarget"], filterMenuController.activeStatusApprovalWorkflowTarget],
      created: [["createdFromTarget", "createdToTarget"], filterMenuController.activeStatusCreatedAtTarget],
      issued: [["issuedFromTarget", "issuedToTarget"], filterMenuController.activeStatusIssuedAtTarget],
      amount: [["amountFromTarget", "amountToTarget"], filterMenuController.activeStatusAmountTarget]
    }

    for (const key in mapping) {
      if (event.target.name.includes(key)) {
        const [targets, statusTarget] = mapping[key]
        if (statusTarget) {
          this.#statusChecker(filterMenuController, targets, statusTarget)
        }
        break
      }
    }
  }

  cancelFilters() {
    this.removeValuesFromFilters()
    this.#constructUrlAndSendRequest()
  }

  removeValuesFromFilters() {
    this.constructor.singleValueTargets.forEach((targetName) => {
      const hasTargetProperty = `has${targetName.charAt(0).toUpperCase() + targetName.slice(1)}Target`
      if (this[hasTargetProperty]) {
        const target = this[`${targetName}Target`]
        target.value = ""
        target.disabled = false
      }
    })

    // Reset empty order number checkbox
    if (this.hasOrderNumberEmptyTarget) {
      this.orderNumberEmptyTarget.checked = false
    }

    // Reset the multi-checkbox groups
    this.#resetCheckboxGroup(this.paymentStatusTargets)
    this.#resetCheckboxGroup(this.expenseTypeTargets)
  }

  cancelChipFilter(event) {
    const targetName = event.currentTarget.dataset.target
    const target = this[`${targetName}Target`]
    if (target) {
      target.value = ""
      target.disabled = false

      // Also clear the empty checkbox if clearing orderNumber
      if (targetName === "orderNumber" && this.hasOrderNumberEmptyTarget) {
        this.orderNumberEmptyTarget.checked = false
      }

      this.#constructUrlAndSendRequest()
    }
  }

  applyFilters(event) {
    this.#constructUrlAndSendRequest({ search: event?.target?.name === "search" })
  }

  removePaymentStatusChip(event) {
    // Namespaced identifiers ("expenses--filter") camel-case oddly through the
    // native dataset API — event.params reads the raw data-*-param attribute
    // directly and is unaffected. Stimulus also runs param values through
    // JSON.parse-style typecasting, so force back to string before comparing
    // against checkbox values (both are always strings).
    this.#uncheckValue(this.paymentStatusTargets, String(event.params.paymentStatus))
    this.#constructUrlAndSendRequest()
  }

  removeExpenseTypeChip(event) {
    // See removePaymentStatusChip for why the param goes through String().
    this.#uncheckValue(this.expenseTypeTargets, String(event.params.expenseType))
    this.#constructUrlAndSendRequest()
  }

  removeLoanChip(event) {
    // See removePaymentStatusChip — Stimulus typecasts a numeric-looking
    // param value to a Number, which would never match the string ids
    // returned by getAll("loan_id[]").
    const loanId = String(event.params.loanId)
    const loanIds = new TableQueryState(window.location.search)
      .toSearchParams()
      .getAll("loan_id[]")
      .filter((id) => id !== loanId)
    const queryState = new TableQueryState(window.location.search).patch({ "loan_id[]": loanIds })
    this.tableNavigator.navigate(queryState, {
      history: "push",
      reason: "filter",
      preserveScroll: { x: true, y: false }
    })
  }

  #constructUrlAndSendRequest({ search = false } = {}) {
    const queryState = this.#constructQueryState()
    if (search) {
      // Deliberately NO restoreFocusSelector, unlike contacts: this list's search
      // field is `data-turbo-permanent`, so Turbo MOVES the same node into the
      // replacement frame (FrameRenderer#render → preservingPermanentElements)
      // and its Bardo re-focuses whatever held focus inside it. Naming a restore
      // target on top of that re-runs table_frame_navigator's
      // setSelectionRange(end, end) up to three times per render, which yanks the
      // caret to the end of the field mid-word. Contacts needs the option because
      // its field is NOT permanent and is genuinely rebuilt.
      this.tableNavigator.scheduleSearch(queryState, { preserveScroll: { x: true, y: false } })
    } else {
      this.tableNavigator.navigate(queryState, {
        history: "push",
        reason: "filter",
        preserveScroll: { x: true, y: false }
      })
    }
  }

  #statusChecker(filterMenuController, targets, statusTarget) {
    const status = targets.some((target) => {
      const targetName = target.replace("Target", "")
      const hasTargetProperty = `has${targetName.charAt(0).toUpperCase() + targetName.slice(1)}Target`
      if (!this[hasTargetProperty]) {
        return false
      }
      const element = this[target]
      if (element.type === "checkbox") {
        return element?.checked
      } else {
        return element.value
      }
    })
    filterMenuController.toggleActiveStatus(status, statusTarget)
  }

  #constructQueryState() {
    const changes = Object.fromEntries(this.constructor.singleValueTargets.map((targetName) => [targetName, null]))
    this.constructor.singleValueTargets.forEach((targetName) => {
      const hasTargetProperty = `has${targetName.charAt(0).toUpperCase() + targetName.slice(1)}Target`
      if (this[hasTargetProperty]) {
        const target = this[`${targetName}Target`]
        if (target?.value && target?.value !== "") {
          changes[targetName] = target.value
        }
      }
    })

    // Handle empty order number checkbox
    if (this.hasOrderNumberEmptyTarget && this.orderNumberEmptyTarget.checked) {
      changes.orderNumber = "empty"
    }

    // Always replace both representations. New URLs use the array parameter,
    // while clearing the legacy scalar prevents an old bookmarked value from
    // surviving a filter update.
    changes.paymentStatus = null
    changes["paymentStatus[]"] = this.#checkedValues(this.paymentStatusTargets)
    changes.expenseType = null
    changes["expenseType[]"] = this.#checkedValues(this.expenseTypeTargets)

    let currentState = new TableQueryState(window.location.search)
    const direction = currentState.toSearchParams().get("direction")
    if (direction && !["asc", "desc"].includes(direction)) {
      currentState = currentState.patch({ direction: null }, { resetPage: false })
    }
    return currentState.patch(changes)
  }

  // Checks the boxes named by the repeated `<key>[]` parameter, falling back to
  // a legacy scalar `<key>` from an old bookmark or saved view.
  #restoreCheckboxGroup(urlSearchParams, key, targets) {
    const selected = urlSearchParams.getAll(`${key}[]`)
    if (selected.length === 0) {
      const legacySelection = urlSearchParams.get(key)
      if (legacySelection) selected.push(legacySelection)
    }
    targets.forEach((box) => {
      box.checked = selected.includes(box.value)
    })
  }

  #resetCheckboxGroup(targets) {
    targets.forEach((box) => {
      box.checked = false
    })
  }

  #checkedValues(targets) {
    return targets.filter((box) => box.checked).map((box) => box.value)
  }

  #uncheckValue(targets, value) {
    const box = targets.find((el) => el.value === value)
    if (box) box.checked = false
  }

  #handleNavigationRollback(event) {
    if (event.detail?.frameId !== this.frameId || event.detail?.tableKey !== "expenses") return
    this.setFilterValues(new TableQueryState(window.location.search))
    if (this.hasExpensesFilterMenuOutlet) this.expensesFilterMenuOutlet.refreshActiveFilterIndicator()
  }
}
